import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Vector as VectorLayer } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { Feature } from 'ol'
import { Point } from 'ol/geom'
import { fromLonLat } from 'ol/proj'
import useMap from 'hooks/use-map'
import { fetchMarkers, fetchMarkerTypes } from 'api/marker'
import { getMarkerStyle } from 'common/ol/styles/marker-style'
import { showModal } from 'actions/app'
import * as c from 'common/c'
import MarkerDialog from 'components/marker/marker-dialog/index'
import CreateMarkerDialog from 'components/marker/create-marker-dialog/index'
import type { MapBrowserEvent } from 'ol'
import type { AppState } from 'state/index.js'
import type { Marker } from 'store/state/marker'

export default function MarkerLayer() {
  const dispatch = useDispatch()
  const map = useMap()

  const markers = useSelector((state: AppState) => state.marker.markers)
  const markerTypes = useSelector((state: AppState) => state.marker.markerTypes)
  const layerVisible = useSelector(
    (state: AppState) => state.marker.layerVisible,
  )
  const visibleTypeIds = useSelector(
    (state: AppState) => state.marker.visibleTypeIds,
  )
  const isCreating = useSelector((state: AppState) => state.marker.isCreating)
  const year = useSelector((state: AppState) => state.field.year)

  const [layer, setLayer] = useState<VectorLayer<VectorSource> | null>(null)

  // Fetch types once on mount
  useEffect(() => {
    dispatch(fetchMarkerTypes() as any)
  }, [])

  // Re-fetch markers when year changes
  useEffect(() => {
    if (year) {
      dispatch(fetchMarkers(year) as any)
    }
  }, [year])

  // Create and manage the OL layer
  useEffect(() => {
    if (!map) return

    const source = new VectorSource()
    const layer = new VectorLayer({
      source,
      zIndex: 10,
      updateWhileAnimating: true,
    })

    map.addLayer(layer)
    setLayer(layer)

    return () => {
      map.removeLayer(layer)
      setLayer(null)
    }
  }, [map])

  // Sync features — also clears when layer is toggled off
  useEffect(() => {
    if (!layer) return

    const source = layer.getSource()!
    source.clear()

    if (!layerVisible) return

    const typeMap = new Map(markerTypes.map((t) => [t.id, t]))

    const visibleMarkers = visibleTypeIds
      ? markers.filter((m) => visibleTypeIds.includes(m.markerTypeId))
      : markers

    const features = visibleMarkers.map((marker: Marker) => {
      const [lon, lat] = marker.geom.coordinates
      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        marker,
      })
      const markerType = typeMap.get(marker.markerTypeId)
      feature.setStyle(getMarkerStyle(markerType?.iconName ?? 'Help'))
      return feature
    })

    source.addFeatures(features)
  }, [layer, layerVisible, markers, markerTypes, visibleTypeIds])

  // Crosshair cursor in create mode
  useEffect(() => {
    const el = map?.getTargetElement() as HTMLElement | undefined
    if (!el) return
    el.style.cursor = isCreating ? 'crosshair' : ''
  }, [isCreating, map])

  // Map click handler
  useEffect(() => {
    if (!map) return

    const handler = (event: MapBrowserEvent<PointerEvent>) => {
      if (isCreating) {
        dispatch({ type: c.MARKER_CREATE_MODE_TOGGLE })
        dispatch(
          showModal(
            <CreateMarkerDialog coordinate={event.coordinate} />,
          ) as any,
        )
        return
      }

      const found = map.forEachFeatureAtPixel(
        event.pixel,
        (feature: any) => {
          const marker = feature.get('marker') as Marker
          dispatch(showModal(<MarkerDialog marker={marker} />) as any)
          return true
        },
        {
          layerFilter: (l: any) => l === layer,
          hitTolerance: 10,
        },
      )
    }

    map.on('singleclick', handler)
    return () => map.un('singleclick', handler)
  }, [map, isCreating, layer])

  return null
}
